import React from 'react'

import { ArticleVideo } from 'component-library/publication/ArticleVideo/ArticleVideo'
import {
    ArticleImage,
    FigcaptionRenderOptions,
} from 'component-library/publication/ArticleImage/ArticleImage'

import {
    AutoplayOptions,
    VideoOptions,
    GetVideoQueue,
} from 'component-library/content/Video/PlayerInterface'
import { VideoDetails } from 'common/client-data-types/VideoDetails'
import { PlayerEvent } from 'common/events/video-event-types'
import { ImageSourceWithCaptions } from 'common/client-data-types/image-item'
import { isFeatureEnabled, useToggleState } from 'common/feature-togglings/features'
import { useProduct } from 'common/hooks/useProduct'
import { useTheme } from '@emotion/react'
import { ConsentLevel } from 'common/data/consent/consent.redux'
import { useConsentGiven } from 'common/hooks/useConsentGiven'
import { HeroVideo } from 'common/client-data-types/HeroVideo'

export interface Props {
    heading: string
    heroImage?: ImageSourceWithCaptions
    heroVideo?: HeroVideo
    onEvent: (event: PlayerEvent) => void
    isVideoStickyEnabled?: boolean
    adUnitPath: string

    /**
     * Allows component to override defaults (including values defaulted by toggles)
     */
    autoplayOptionsOverrides?: false | Partial<AutoplayOptions>
    autoplayNextOptionssOverrides?: false | Partial<AutoplayOptions>

    showVideoTitles?: boolean
    getVideoQueue: GetVideoQueue
    publicationPathname?: string

    preferredCaptionSource?: 'image' | 'video'
    figcaptionRenderOptions?: FigcaptionRenderOptions
    imageRenderType?: 'feature'
}

export const ArticleHero = (props: Props) => {
    const {
        showVideoTitles,
        heroVideo,
        heroImage,
        isVideoStickyEnabled,
        getVideoQueue,
        publicationPathname,
        adUnitPath,
        autoplayOptionsOverrides = {},
        autoplayNextOptionssOverrides = {},
        preferredCaptionSource = 'video',
        figcaptionRenderOptions,
        imageRenderType,
    } = props
    const theme = useTheme()
    const product = useProduct()
    const toggles = useToggleState()

    const canLoadVideoPlayer = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Advertising | ConsentLevel.Analytics,
    })

    if (canLoadVideoPlayer && heroVideo) {
        const { videoId, heading } = heroVideo

        const videoDetails: VideoDetails = {
            id: videoId,
            accountId: heroVideo.accountId,
            heading,
            posterImage: heroVideo.posterImage,
            source: heroVideo.source,
            videoType: heroVideo.videoType,
        }

        const isAutoPlay = isFeatureEnabled(toggles, 'can-autoplay-video', true)
        const isAutoPlayNext = isFeatureEnabled(toggles, 'can-autoplay-next')
        const allowMutedAutoplay = isFeatureEnabled(toggles, 'can-autoplay-muted-video', true)

        const autoplayOptions: false | AutoplayOptions =
            autoplayOptionsOverrides !== false && isAutoPlay
                ? {
                      allowMuted: allowMutedAutoplay,
                      delayDuration: 3,
                      pauseCountdownOutsideViewport: true,
                      ...autoplayOptionsOverrides,
                  }
                : false
        const autoplayNextOptions: false | AutoplayOptions =
            isAutoPlayNext && autoplayNextOptionssOverrides !== false
                ? { delayDuration: 3, ...autoplayOptions, ...autoplayNextOptionssOverrides }
                : false

        const videoOptions: VideoOptions = {
            adUnitPath,
            canAutoplayVideos: false,
            enableSnowplow: isFeatureEnabled(toggles, 'snowplow'),
            enablePrebid: isFeatureEnabled(toggles, 'prebid-video'),
            enableNielsenVideoReporting: isFeatureEnabled(toggles, 'nielsen-video-events'),
        }

        return (
            <ArticleVideo
                showTitles={Boolean(showVideoTitles)}
                videoDetails={videoDetails}
                autoplayOptions={autoplayOptions}
                autoplayNextOptions={autoplayNextOptions}
                videoOptions={videoOptions}
                captionText={getPreferredCaption(preferredCaptionSource, heroImage, heroVideo)}
                credit={
                    heroImage === undefined || product === 'sevennews'
                        ? getPreferredCredit(preferredCaptionSource, heroImage, heroVideo)
                        : undefined
                }
                isHeroVideo={true}
                showPlaylist={isFeatureEnabled(toggles, 'video-playlist')}
                pauseOverlay
                getVideoQueue={getVideoQueue}
                isVideoStickyEnabled={isVideoStickyEnabled}
                onEvent={props.onEvent}
                publicationPathname={publicationPathname}
                figcaptionRenderOptions={figcaptionRenderOptions}
            />
        )
    }

    if (heroImage) {
        return (
            <ArticleImage
                image={heroImage}
                figCaption={{
                    byline: heroImage.byline,
                    captionText: heroImage.captionText,
                    credit: heroImage.credit,
                    kind: 'image',
                }}
                isHeroImage={true}
                figcaptionRenderOptions={figcaptionRenderOptions}
                imageRenderType={imageRenderType}
            />
        )
    }

    return (
        <ArticleImage
            image={theme.fallbackImages}
            isHeroImage={true}
            figcaptionRenderOptions={figcaptionRenderOptions}
            imageRenderType={imageRenderType}
        />
    )
}
ArticleHero.displayName = 'ArticleHero'

// 7NEWS would like unrelated videos to still appear with the caption of the main image
export function getPreferredCaption(
    preferredCaptionSource: 'image' | 'video',
    heroImage: ImageSourceWithCaptions | undefined,
    heroVideo: HeroVideo | undefined
) {
    if (preferredCaptionSource === 'video') {
        return heroVideo ? heroVideo.captionText : heroImage ? heroImage.captionText : undefined
    }

    return heroImage ? heroImage.captionText : heroVideo ? heroVideo.captionText : undefined
}

// 7NEWS would like unrelated videos to still appear with the credit of the main image
export function getPreferredCredit(
    preferredCaptionSource: 'image' | 'video',
    heroImage: ImageSourceWithCaptions | undefined,
    heroVideo: HeroVideo | undefined
) {
    const heroVideoCredit =
        heroVideo && heroVideo.posterImage && heroVideo.posterImage.credit
            ? heroVideo.posterImage.credit
            : undefined
    const heroImageCredit = heroImage && heroImage.credit ? heroImage.credit : undefined

    if (preferredCaptionSource === 'video') {
        return heroVideoCredit ? heroVideoCredit : heroImageCredit
    }

    return heroImageCredit ? heroImageCredit : heroVideoCredit
}
